import React from "react";
import {
	SEGMENT_CMC,
	URL_CMC_AIRCRAFT_ACCIDENTS_AND_INCIDENTS_DATA,
	URL_CMC_AIRCRAFT_DATA,
	URL_CMC_AIRCRAFT_OWNERSHIP_DATA,
	URL_CMC_AIRFRAME_MRO_EVENT_DATA,
	URL_DATA_FEEDS,
} from "../constants";
import { Hero } from "../sections/hero/Hero";
import { ProudToServe } from "../sections/proud-to-serve/ProudToServe";
import { Technology } from "../sections/Technology";
import { Testimonials } from "../../common/layout/testimonials/Testimonials";
import { ProductTabs } from "../sections/ProductTabs";
import { ProductSlider } from "../sections/ProductSlider";
import { Upgrades } from "../sections/Upgrades";
import { Trial } from "../sections/Trial";
import { Pricing } from "../sections/pricing/Pricing";
import { Faq } from "../sections/faq/Faq";
import { productSegmentPageProps } from "../../common/types/product";

export default function CommercialAircraftHistoryData({
	packageName,
	subscribeLink,
	tabsNav,
	testimonials,
}: productSegmentPageProps) {
	return (
		<>
			<Hero
				title={packageName}
				subtitle="Search for full historical operator data down to an aircraft level and get access to full aircraft operator history for all inactive aircraft from 2000 up to the present day."
				packages={[
					{
						label: SEGMENT_CMC,
						features: [
							"Search historical operator data for more than 56,000 commercial aircraft",
							"Track aircraft ownership histories and operator fleet growth trends over time",
						],
						subscribeLinkUrl: subscribeLink,
					},
				]}
			/>
			{tabsNav}
			<ProudToServe />
			<ProductTabs
				title={packageName}
				tabs={[
					{
						title: "Aircraft History",
						subtitle:
							"Get insights into aircraft history on an aircraft level including:",
						list: [
							"An operator’s fleet development overview",
							"An operator’s fleet delivery by year (new, used, and overall aircraft deliveries)",
							"Full aircraft operator history for all inactive aircraft from 2000 up to the present day",
							"An operator overview on an aircraft family level",
							"Annual deliveries of a specific aircraft variant",
							"Annual active aircraft by variant",
						],
						visual: {
							url: "/static/img/product/commercial-aircraft-history-data/aircraft-profile-overview.jpg",
						},
					},
				]}
			/>
			<ProductSlider
				description="Search for aircraft worldwide using a range of filters, such as location, operator, aircraft type, engine type, dates and more."
				slides={[
					{
						title: "Location",
						description:
							"Search for aircraft history based on the operator location:",
						list: [
							"Operator continent",
							"Operator country",
							"Operator region",
						],
						visual: {
							url: "/static/img/product/commercial-aircraft-history-data/aircraft-history-search-by-location.jpg",
							title: "Global operator fleet overview in 2010",
						},
					},
					{
						title: "Operator",
						description:
							"Search for a specific operator’s history by:",
						list: [
							"Including or excluding multiple operators",
							"Operator name",
						],
						visual: {
							url: "/static/img/product/commercial-aircraft-history-data/aircraft-history-search-by-operator.jpg",
							title: "British Airways historic fleet overview",
						},
					},
					{
						title: "Aircraft Type",
						description: "Search for aircraft history by:",
						list: [
							"Aircraft market group",
							"Aircraft manufacturer",
							"Aircraft production line",
							"Aircraft family/type/variant",
						],
						visual: {
							url: "/static/img/product/commercial-aircraft-history-data/aircraft-profile-historical-overview.jpg",
							title: "Historical development of aircraft variant A320-200",
						},
					},
					{
						title: "Aircraft",
						description: "Search for aircraft history based on:",
						list: [
							"Aircraft registration",
							"Line/serial number",
							"Aircraft hexcode",
						],
						visual: {
							url: "/static/img/product/commercial-aircraft-history-data/aircraft-history-search-by-aircraft.jpg",
							title: "Operator history of the aircraft registration XA-UQX",
						},
					},
					{
						title: "Engines",
						description:
							"Search for aircraft history based on the engines installed:",
						list: [
							"Engine manufacturer",
							"Engine type",
							"Engine subtype",
						],
						visual: {
							url: "/static/img/product/commercial-aircraft-history-data/aircraft-history-search-by-engines.jpg",
							title: "Overview of all aircraft powered by CFM International engines, in operation in 2020",
						},
					},
					{
						title: "Dates",
						description:
							"Search for aircraft history overview based on a specific time range",
						list: [
							"As of date – specific search period",
							"Date in - date the aircraft was delivered to the operator (regardless of other changes such as registration, variant, etc.)",
							"Date out – date the aircraft left the operator (regardless of other changes such as registration, variant, etc.)",
						],
						visual: {
							url: "/static/img/product/commercial-aircraft-history-data/aircraft-history-search-by-dates.jpg",
							title: "List of all aircraft that stopped operating in 2020",
						},
					},
				]}
			/>
			<Upgrades
				packageName={packageName}
				modules={[
					{
						title: "Aircraft Data",
						content:
							"Get access to our extensive fleet database with over 82,000 aircraft currently being tracked",
						url: URL_CMC_AIRCRAFT_DATA,
					},
					{
						title: "Aircraft Ownership Data",
						content:
							"Get insights into aircraft ownership, management, valuation estimates, full lessor portfolios, and SPV-financed aircraft details.",
						url: URL_CMC_AIRCRAFT_OWNERSHIP_DATA,
					},
					{
						title: "Aircraft Accidents and Incidents Data",
						content:
							"Get a detailed overview of a commercial aviation aircraft accident/incident on a tail level.",
						url: URL_CMC_AIRCRAFT_ACCIDENTS_AND_INCIDENTS_DATA,
					},
					{
						title: "Airframe MRO Events Data",
						content:
							"Get insights into over 50,000 airframe maintenance events worldwide ",
						url: URL_CMC_AIRFRAME_MRO_EVENT_DATA,
					},
				]}
			>
				<Trial subscribeLink={subscribeLink} />
			</Upgrades>
			<Testimonials testimonials={testimonials} />
			<Technology />
			<Pricing
				packageName={packageName}
				description="Gain a comprehensive understanding of the aviation industry with detailed, up-to-date insights."
				list={[
					"Search historical operator data for more than 56,00 commercial aircraft",
					"Track aircraft ownership histories and operator fleet growth trends over time",
				]}
				subscribeLink={subscribeLink}
				downloadsDescription="Our Commercial Aviation Aircraft History Data is ready for instant, unlimited downloads either as Excel, CSV or JSON file."
			>
				<Trial subscribeLink={subscribeLink} />
			</Pricing>
			<Faq
				items={[
					{
						title: "Can I download this data set?",
						content:
							"The Commercial Aviation Aircraft History Data includes an optional feature available for purchase that allows unlimited downloads in CSV, XLS, or JSON formats.",
						linkUrl: "mailto:sales@ch-aviation.com",
					},
					{
						title: "Can I integrate this data as a data feed into my own system?",
						content: (
							<p>
								Yes, Commercial Aviation Aircraft History Data
								can be purchased as a data feed for seamless
								integration into your own system. ch-aviation
								also provides full support for the integration
								process. To learn more about data feeds, click{" "}
								<a
									href={URL_DATA_FEEDS}
									target="_blank"
									rel="noopener noreferrer"
								>
									here
								</a>
								.
							</p>
						),
					},
					{
						title: "How well is the historical dimension covered?",
						content: (
							<ul className="ticks ticks--small">
								<li>
									If an aircraft is with an operator – full
									historical data is presented
								</li>
								<li>
									For all inactive aircraft that operated
									during the 21st century (2000-today), we
									show the history back till the year 2000.
								</li>
								<li>
									Aircraft built and retired before 2000 – no
									historical data is presented.
								</li>
							</ul>
						),
					},
					{
						title: "How often is the data updated?",
						content:
							"The Commercial Aviation Aircraft History Data is being updated weekly, ensuring you always have the latest information.",
					},
				]}
			/>
		</>
	);
}
